import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Dashboard, Progress } from '../../api/queries';
import type { ContentView } from '../../lib/content';
import { entranceDelay, type EntranceState, useEntrance } from '../../lib/motion';
import { arcPath } from '../charts/geometry';
import s from './dashboard.module.css';
import {
  type CoverageBar,
  coverageByGroup,
  coverageShade,
  type DashMember,
  deltaTone,
  formatDelta,
  PERIOD_PROGRESS_TOP,
  periodProgress,
  progressScale,
  riskEdge,
  roseSlots,
  scaleAt,
} from './dashboard-model';

/** Rose geometry (design v3): centre, inner and outer radius, in the SVG's own units. */
const ROSE = { c: 200, r0: 40, r1: 166 };

/**
 * The coverage rose (design v3): one petal per area, as long as the share of the team at L4+ (confirmed),
 * red where the area is a risk, grouped like the content with a gap between groups. Each group is an image
 * labelled with its areas and head counts, which is the chart's text alternative. Petals grow out of the
 * centre during the page entrance.
 */
export function CoverageRose({
  coverage,
  content,
  teamSize,
}: {
  coverage: Dashboard['coverage'];
  content: ContentView;
  teamSize: number;
}) {
  const { t } = useTranslation();
  const e = useEntrance();
  const groups = coverageByGroup(coverage, content);
  const barText = (b: CoverageBar) =>
    t('dashboard.coverageBar', { area: b.areaId, name: b.name, n: b.count });
  const { c, r0, r1 } = ROSE;
  const slots = roseSlots(groups.map((g) => g.bars.length));
  const max = Math.max(2, teamSize);
  const radius = (n: number) => r0 + Math.min(1, n / max) * (r1 - r0);
  const edge = riskEdge(coverage);
  let k = 0;
  return (
    <svg className={s.rose} viewBox="-58 14 516 372" role="group" aria-label={t('dashboard.coverageChart')}>
      {[0.5, 1].map((f) => (
        <circle key={f} cx={c} cy={c} r={r0 + f * (r1 - r0)} className={s.roseGrid} />
      ))}
      {groups.map((g, gi) => {
        const layout = slots[gi];
        if (!layout) return null;
        const cos = Math.cos(layout.mid);
        return (
          <g key={g.groupId} role="img" aria-label={g.bars.map(barText).join('; ')}>
            {g.bars.map((b, i) => {
              const { a0, a1 } = layout.petals[i] ?? { a0: 0, a1: 0 };
              const grow = petalEntrance(e, c, 150 + k++ * 16);
              // The title sits on the whole slot (track and petal), so hovering anywhere along it shows it.
              return (
                <g key={b.areaId} className={s.roseSlot}>
                  <title>{barText(b)}</title>
                  <path d={arcPath(c, c, r0, r1, a0, a1)} className={s.roseTrack} />
                  <path
                    d={arcPath(c, c, r0, b.count ? radius(b.count) : r0 + 5, a0, a1)}
                    className={s.rosePetal}
                    data-shade={b.risk ? 'risk' : coverageShade(b.count, teamSize)}
                    style={grow}
                  />
                </g>
              );
            })}
            <text
              x={c + cos * (r1 + 12)}
              y={c + Math.sin(layout.mid) * (r1 + 12) + 4}
              textAnchor={cos > 0.25 ? 'start' : cos < -0.25 ? 'end' : 'middle'}
              className={s.roseLabel}
            >
              {`${g.n}. ${g.short}`}
            </text>
          </g>
        );
      })}
      {edge === null ? null : <circle cx={c} cy={c} r={radius(edge)} className={s.roseEdge} />}
      <text x={c} y={c + 4} textAnchor="middle" className={s.roseCenter}>
        {t('dashboard.coverageCenter')}
      </text>
    </svg>
  );
}

/** A petal grows out of the centre at `delayMs` of the entrance. */
function petalEntrance(e: EntranceState, c: number, delayMs: number): CSSProperties | undefined {
  if (e.settled) return undefined;
  const dl = entranceDelay(e, delayMs);
  return {
    transformBox: 'view-box',
    transformOrigin: `${c}px ${c}px`,
    transform: e.entered ? 'scale(1)' : 'scale(0.24)',
    opacity: e.entered ? 1 : 0,
    transition: `transform 0.75s var(--ease-enter) ${dl}, opacity 0.4s ease ${dl}`,
  };
}

/** The rose's key: petals shaded by head count, red for risk. */
export function CoverageLegend({ riskCount }: { riskCount?: number }) {
  const { t } = useTranslation();
  return (
    <div className={s.covLegend}>
      <span>
        <span className={`${s.covKey} ${s.covKeyOk}`} />
        {t('dashboard.coverageLegendOk')}
      </span>
      <span>
        <span className={`${s.covKey} ${s.covKeyRisk}`} />
        {riskCount === undefined
          ? t('dashboard.coverageLegendRisk')
          : t('dashboard.coverageLegendRiskN', { n: riskCount })}
      </span>
    </div>
  );
}

/**
 * "4. Tiến bộ trong kỳ" as dumbbells: last period's confirmed score (hollow) to this period's (filled) on a
 * scale shared by every row, then the change and the work taken outside the comfort zone. Biggest gain
 * first; the top PERIOD_PROGRESS_TOP until "Show all".
 */
export function PeriodProgress({
  members,
  progress,
  periodId,
  onOpen,
  bare,
}: {
  members: readonly DashMember[];
  progress: Progress;
  periodId: string;
  onOpen: (memberId: string) => void;
  /** Inside the dashboard's detail sheet: no card, the sheet's title stands for the heading. */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const [all, setAll] = useState(bare === true);
  const rows = periodProgress(members, progress, periodId);
  const scale = progressScale(rows);
  const at = (score: number) => `${scaleAt(score, scale)}%`;
  const shown = all ? rows : rows.slice(0, PERIOD_PROGRESS_TOP);
  return (
    <section className={`${s.card} ${s.padded}`} data-bare={bare} aria-labelledby="dash-period">
      <div className={bare ? 'visually-hidden' : s.sectionTitle}>
        <h2 id="dash-period">{t('dashboard.periodTitle')}</h2>
        <p>{t('dashboard.periodNote')}</p>
      </div>
      <div id="dash-period-list" className={s.progList}>
        {shown.map(({ member, prev, now, delta }) => {
          const tone = deltaTone(delta);
          const fromTo = `${prev ?? '–'} → ${now}`;
          const outside = t('dashboard.outsideCount', { n: member.outsideWork });
          return (
            <button
              key={member.id}
              type="button"
              className={s.progRow}
              title={fromTo}
              onClick={() => {
                onOpen(member.id);
              }}
            >
              <span className={s.name}>{member.displayName}</span>
              <span className={s.dumbbell} data-tone={tone} aria-hidden="true">
                <span className={s.dbTrack} />
                {prev === null ? null : (
                  <>
                    <span
                      className={s.dbSeg}
                      style={{
                        left: at(Math.min(prev, now)),
                        width: `${scaleAt(Math.max(prev, now), scale) - scaleAt(Math.min(prev, now), scale)}%`,
                      }}
                    />
                    <span className={s.dbPrev} style={{ left: at(prev) }} />
                  </>
                )}
                <span className={s.dbNow} style={{ left: at(now) }} />
              </span>
              <span className={s.delta} data-tone={tone}>
                {formatDelta(delta)}
              </span>
              <span className={s.outside} title={outside} aria-hidden="true">
                {Array.from({ length: member.outsideWork }, (_, k) => (
                  <span key={k} className={s.outsideBox} />
                ))}
              </span>
              <span className="visually-hidden">{` · ${fromTo} · ${outside}`}</span>
            </button>
          );
        })}
      </div>
      <div className={s.progFoot}>
        <span className={s.progLegend}>
          <span>
            <span className={s.dbPrevKey} />
            {t('dashboard.legendPrevPeriod')}
          </span>
          <span>
            <span className={s.dbNowKey} />
            {t('dashboard.legendThisPeriod')}
          </span>
          <span>
            <span className={s.outsideBox} />
            {t('dashboard.outsideLegend')}
          </span>
        </span>
        {rows.length > PERIOD_PROGRESS_TOP ? (
          <button
            type="button"
            className={`${s.ghostButton} ${s.moreButton}`}
            aria-expanded={all}
            aria-controls="dash-period-list"
            onClick={() => {
              setAll((v) => !v);
            }}
          >
            {all ? t('dashboard.periodShowFewer') : t('dashboard.periodShowAll', { n: rows.length })}
          </button>
        ) : null}
      </div>
    </section>
  );
}
